import React, { useEffect, useState } from "react";
import GlobalDialog from "../../../../components/mui/Dialogue/GlobalDialog";
import {
  AddButton,
  CancelButton,
  SubmitButton,
  UpdateButton,
} from "../../../../components/mui/Buttons/Buttons";
import { useFormik, Form, FormikProvider } from "formik";
import { Autocomplete, Box, Button, Stack, TextField } from "@mui/material";
import GlobalSelectField from "../../../../components/mui/GlobalSelectField";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import {
  addSessionStartDate,
  clearSessionStartDate,
  getSessionstartdate,
  getSubjectEnrollmentId,
  updatesessionstartdate,
  UpdateStudentStatus,
} from "../../../../redux/features/croSlice";
import { Icon } from "@iconify/react/dist/iconify.js";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { savePaginationData } from "../../../../redux/features/globalDatagridSlice";

// onSaved (optional): runs after a successful save. renderTrigger (optional): (open) => button, replacing the
// default green button (the retention workspace, stage 7).
const CROsessionstartdate = ({ rowData, courseenrollmentId, onSaved, renderTrigger }) => {
  const [open, setOpen] = useState(false);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState(dayjs());

  const { loading, sessionstartdate, subjectEnrollId } = useSelector(
    (state) => state.cro
  );
  const { paginationData } = useSelector((state) => state.globalDataGrid);

  const ValidationSchema = Yup.object().shape({
    subject_enrollment_id: Yup.string().required("Please select subject"),

    session_start_date: Yup.string()
      .nullable()
      .required("Please select start date"),

    firstsession_starttime: Yup.string()
      .nullable()
      .required("Please select start time"),

    firstsession_endtime: Yup.string()
      .nullable()
      .required("Please select end time"),
  });

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      session_start_date: sessionstartdate?.session_start_date
        ? dayjs(sessionstartdate.session_start_date)
        : null,
      firstsession_starttime: sessionstartdate?.firstsession_starttime
        ? dayjs(sessionstartdate.firstsession_starttime, "HH:mm:ss")
        : null,
      firstsession_endtime: sessionstartdate?.firstsession_endtime
        ? dayjs(sessionstartdate.firstsession_endtime, "HH:mm:ss")
        : null,
      subject_enrollment_id: sessionstartdate?.subject_enrollment_id || "",
    },
    validationSchema: ValidationSchema,

    onSubmit: (values) => {
      const payload = {
        ...values,
        session_start_date: dayjs(values.session_start_date).format(
          "YYYY-MM-DD"
        ),
        firstsession_starttime: dayjs(values.firstsession_starttime).format(
          "HH:mm:ss"
        ),
        firstsession_endtime: dayjs(values.firstsession_endtime).format(
          "HH:mm:ss"
        ),
      };
      if (sessionstartdate?.session_start_date) {
        dispatch(updatesessionstartdate(payload)).then((res) => {
          if (res.payload.status === "success") {
            enqueueSnackbar(res.payload.message, {
              variant: "success",
            });
            setOpen(false);
            dispatch(clearSessionStartDate());
            dispatch(
              savePaginationData({ ...paginationData, openAnchor: false })
            );
            onSaved?.();
          } else {
            enqueueSnackbar(res.payload.message, {
              variant: "error",
            });
            // setOpen(false);
          }
        });
      } else {
        dispatch(addSessionStartDate(payload)).then((res) => {
          if (res.payload.status === "success") {
            enqueueSnackbar(res.payload.message, {
              variant: "success",
            });
            dispatch(clearSessionStartDate());
            setOpen(false);
            dispatch(
              savePaginationData({ ...paginationData, openAnchor: false })
            );
            onSaved?.();
          } else {
            enqueueSnackbar(res.payload.message, {
              variant: "error",
            });
            // setOpen(false);
          }
        });
      }
    },
  });

  useEffect(() => {
    dispatch(clearSessionStartDate());
    dispatch(getSubjectEnrollmentId(courseenrollmentId));
  }, [courseenrollmentId]);

  return (
    <GlobalDialog
      title={"Session Start Date"}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={"md"}
      closeButton={true}
      popupButton={
        renderTrigger ? renderTrigger(() => setOpen(true)) : (
        <Button
          sx={{
            p: "7px",
            px: "10px",
            gap: "5px",
            background: "linear-gradient(to right,#7AC74F, #329F5B)",
            color: "#fff",
            "&:hover": {
              background: "linear-gradient(to right,#59A96A, #3F612D)",
            },
          }}
          onClick={() => setOpen(true)}
        >
          <Icon icon="bi:calendar2-date-fill" width="20" height="20" />
          Session Start Date
        </Button>
        )
      }
      content={
        <>
          <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
              <Box>
                <Stack direction={"column"} spacing={2} marginBottom={2}>
                  <GlobalSelectField
                    options={{
                      formik,
                      name: "subject_enrollment_id",
                      label: "Subject Enrollment",
                      data: subjectEnrollId.data?.map((item) => ({
                        ...item,
                        course_name: `${item.course_name} - ${item.subject_name}`,
                      })),
                      keys: {
                        id: "subject_enrollment_id",
                        value: "course_name",
                      },
                    }}
                    onChange={(subjectEnrollment) => {
                      dispatch(getSessionstartdate(subjectEnrollment));
                    }}
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Date"
                        value={formik.values.session_start_date}
                        onChange={(newValue) => {
                          formik.setFieldValue("session_start_date", newValue);
                        }}
                        onBlur={formik.handleBlur}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error:
                              formik.touched.session_start_date &&
                              Boolean(formik.errors.session_start_date),
                            helperText:
                              formik.touched.session_start_date &&
                              formik.errors.session_start_date,
                          },
                        }}
                        minDate={dayjs()}
                      />
                    </LocalizationProvider>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        name="firstsession_starttime"
                        label="Start Time"
                        ampm={false}
                        value={formik.values.firstsession_starttime}
                        onChange={(newValue) =>
                          formik.setFieldValue(
                            "firstsession_starttime",
                            newValue
                          )
                        }
                        onBlur={() =>
                          formik.setFieldTouched("firstsession_starttime", true)
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            onKeyDown: (e) => e.preventDefault(),
                            error:
                              formik.touched.firstsession_starttime &&
                              Boolean(formik.errors.firstsession_starttime),
                            helperText:
                              formik.touched.firstsession_starttime &&
                              formik.errors.firstsession_starttime,
                          },
                        }}
                      />
                    </LocalizationProvider>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        name="firstsession_endtime"
                        label="End Time"
                        ampm={false}
                        value={formik.values.firstsession_endtime}
                        onChange={(newValue) =>
                          formik.setFieldValue("firstsession_endtime", newValue)
                        }
                        onBlur={() =>
                          formik.setFieldTouched("firstsession_endtime", true)
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            onKeyDown: (e) => e.preventDefault(),
                            error:
                              formik.touched.firstsession_endtime &&
                              Boolean(formik.errors.firstsession_endtime),
                            helperText:
                              formik.touched.firstsession_endtime &&
                              formik.errors.firstsession_endtime,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Stack>
                </Stack>
                {sessionstartdate?.session_start_date ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Button
                      sx={{
                        background: "#F59E0B",
                        color: "#fff",
                        px: 2,
                        "&:hover": {
                          background: "#F59E0B",
                        },
                      }}
                    >
                      Session start date already added, please update the date
                    </Button>
                  </Box>
                ) : null}

                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}
                >
                  <Stack direction={"row"} spacing={1}>
                    <CancelButton size="small" action={() => setOpen(false)}>
                      Cancel
                    </CancelButton>
                    {sessionstartdate?.session_start_date ? (
                      <UpdateButton
                        disabled={loading}
                        size="small"
                        type="submit"
                      >
                        {loading ? "Please wait..." : "Update"}
                      </UpdateButton>
                    ) : (
                      <SubmitButton
                        disabled={loading}
                        size="small"
                        type="submit"
                      >
                        {loading ? "Please wait..." : "Submit"}
                      </SubmitButton>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Form>
          </FormikProvider>
        </>
      }
    />
  );
};

export default CROsessionstartdate;
